import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  HttpProblemError,
  PasswordSchema,
  RecoverRequestSchema,
  type RecoverRequest,
} from '@collector/shared'
import { z } from 'zod'
import { api } from '../lib/api'

export function RecoverPage() {
  const { token } = useParams<{ token?: string }>()
  if (token) return <ResetForm token={token} />
  return <RequestForm />
}

function RequestForm() {
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RecoverRequest>({ resolver: zodResolver(RecoverRequestSchema) })

  const onSubmit = async (values: RecoverRequest) => {
    await api().auth.recover(values)
    setSubmitted(true)
  }

  return (
    <Card title={t('recover.requestTitle')}>
      {submitted ? (
        <p className="text-sm text-stone-700 dark:text-stone-200">{t('recover.requestSent')}</p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e)
          }}
        >
          <div>
            <label className="label">{t('login.email')}</label>
            <input className="input" type="email" {...register('email')} />
            {errors.email ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email.message}</p>
            ) : null}
          </div>
          <button className="btn-primary w-full" disabled={isSubmitting}>
            {t('recover.sendReset')}
          </button>
        </form>
      )}
      <p className="mt-3 text-xs text-stone-500 dark:text-stone-400">
        <Link to="/login" className="underline">
          {t('recover.backToSignIn')}
        </Link>
      </p>
    </Card>
  )
}

const ResetSchema = z.object({ password: PasswordSchema })
type ResetForm = z.infer<typeof ResetSchema>

function ResetForm({ token }: { token: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetForm>({ resolver: zodResolver(ResetSchema) })

  const onSubmit = async (values: ResetForm) => {
    setServerError(null)
    try {
      await api().auth.resetWithToken(token, values)
      void navigate('/login', { replace: true })
    } catch (err) {
      setServerError(
        err instanceof HttpProblemError ? err.problem.title : t('common.unexpectedError'),
      )
    }
  }

  return (
    <Card title={t('recover.newPasswordTitle')}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e)
        }}
      >
        <div>
          <label className="label">{t('recover.newPasswordLabel')}</label>
          <input className="input" type="password" {...register('password')} />
          {errors.password ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.password.message}</p>
          ) : null}
        </div>
        {serverError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{serverError}</p>
        ) : null}
        <button className="btn-primary w-full" disabled={isSubmitting}>
          {t('recover.update')}
        </button>
      </form>
    </Card>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-stone-50 px-4 dark:bg-stone-950">
      <div className="w-full max-w-sm rounded-lg border border-stone-200 bg-white p-6 shadow-sm dark:border-stone-700 dark:bg-stone-900">
        <h1 className="mb-3 text-lg font-semibold text-stone-900 dark:text-stone-100">{title}</h1>
        {children}
      </div>
    </div>
  )
}
